<template>
  <div>
    <div class="payout-bulk-generation">
      <div class="row">
        <div class="col col-12 col-md-2">
          <label class="form__label">Date From:</label>
          <Datepicker
            :bootstrap-styling="true"
            format="yyyy-MM-dd"
            :value="_toIsoStringTime(dateFrom)"
            @input="dateFrom = _toLocalTime($event, defaultDateFormat)"
          />
        </div>
        <div class="col col-12 col-md-2">
          <label class="form__label">Date To:</label>
          <Datepicker
            :bootstrap-styling="true"
            format="yyyy-MM-dd"
            :value="_toIsoStringTime(dateTo)"
            @input="dateTo = _toLocalTime($event, defaultDateFormat)"
          />
        </div>
        <div class="col col-12 col-md-2">
          <label class="form__label">USD / UAH</label>
          <b-form-input
            type="number"
            v-model="currencyRates.UAH"
            debounce="300"
          />
        </div>
        <div class="col col-12 col-md-2">
          <label class="form__label">USD / EUR</label>
          <b-form-input
            type="number"
            v-model="currencyRates.EUR"
            debounce="300"
          />
        </div>
        <div class="col col-12 col-md-2">
          <label class="form__label">USD / GBP</label>
          <b-form-input
            type="number"
            v-model="currencyRates.GBP"
            debounce="300"
          />
        </div>
        <div class="col col-12 col-md-2 align-self-end">
          <b-button
            type="submit"
            class="button ml-2 float-right"
            variant="primary"
            @click.prevent="generatePayouts">
            Generate payouts
          </b-button>
        </div>
      </div>
      <div class="d-flex justify-content-center">
        <div v-if="isRatesLoading" class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
    </div>
    <b-modal
      size="lg"
      hide-footer
      v-model="isShowCreationModal"
      class="modal"
      @hide="hideCreationModal"
    >
      <template v-if="isDataLoading === false">
        <h6 v-b-toggle.collapse-gen-by-worklogs>Generation based on non-covered Jira worklogs</h6>
        <b-collapse visible id="collapse-gen-by-worklogs" class="mt-2">
          <b-card>
            <div
              class="row mb-2"
              v-for="(message, index) in messages"
              :key="index"
            >
                <div class="col col-12 col-md-8">
                  <div v-html="message.text"></div>
                  <div
                    v-if="message.errors && message.errors.length"
                    class="small pl-2 status-error">{{ message.errors.join(' | ') }}
                  </div>
                </div>
                <div class="col col-12 col-md-2 message-status">
                  <span v-if="message.loading" class="small-spinner spinner-border text-secondary" role="status">
                    <span class="sr-only">Creating...</span>
                  </span>
                  <span v-else-if="message.status === 'success'" class="status-success status-icon">&check;</span>
                  <span
                    v-else-if="message.status === 'error'"
                    class="status-error status-icon"
                  >&#9888;</span>
                  <span
                    v-else-if="message.status === 'warning'"
                    class="status-warning status-icon"
                  >&#9888;</span>
                  <span
                    v-if="message.item && message.item.extra_payments.length"
                    class="ep-message"
                    v-b-tooltip.html :title="'This payout has extra payments copied from last payee payout'"
                  >EP <font-awesome-icon icon="info" style="font-size: 0.7rem;"/></span>
                  <template v-if="message.item && message.item.indicators && Object.values(message.item.indicators).length">
                    <span
                      v-for="(items, indicator) in message.item.indicators"
                      :key="indicator"
                      class="ep-message"
                      v-b-tooltip.html :title="indicatorTooltip(items)"
                    >{{ indicator }} <font-awesome-icon icon="info" style="font-size: 0.7rem;"/></span>
                  </template>
                </div>
                <div class="col col-12 col-md-2">
                  <PayoutsLinks
                    v-if="message.item"
                    :templates="templates"
                    :item="message.item"
                    @updatePayouts="updatePayouts"
                    @showEditPayoutsModal="showPayoutModalManual(message)"
                    @markAsPaid="markPaid($event, message.item)"
                  />
                </div>
            </div>
            <div class="d-flex justify-content-center mt-2">
                <button
                  class="btn button btn-primary"
                  v-if="isProcessedCreation && payoutsForCreationCount > 0"
                  @click="isProcessedCreation = false">Stop creation ({{ payoutsForCreationCount }} left)</button>
                <button
                  v-else-if="payoutsForCreationCount > 0 && !isSomePayoutCreating"
                  class="btn button btn-primary"
                  @click="processCreation()">Create {{ payoutsForCreationCount }} payouts</button>
            </div>
          </b-card>
        </b-collapse>
        <br />
        <h6 v-b-toggle.collapse-copy-extra-paymented>Suggested payouts without worklogs (duplicate from last 2 months)</h6>
        <b-collapse visible id="collapse-copy-extra-paymented" class="mt-2">
          <b-card>
            <template v-if="copyExtraPaymentedPayouts.length">
              <div
                class="row mb-2"
                v-for="({ original, item, status }, index) in copyExtraPaymentedPayouts"
                :key="index"
              >
                <div class="col col-12 col-md-5">
                  {{ original.payee_name }} {{ _toLocalTime(original.issue_date, defaultDateFormat) }}
                  <PayoutsLinks
                    style="float:right;"
                    v-if="original"
                    :item="original"
                    @updatePayouts="updatePayouts"
                    @showEditPayoutsModal="showPayoutModalManual({ item: original })"
                    @markAsPaid="markPaid($event, original)"
                  />
                </div>
                <div class="col col-12 col-md-2 message-status text-center">
                  <div v-if="status === 'new'"
                       class="button btn btn-success"
                       @click.prevent="copyPayout(index)">
                    <font-awesome-icon icon="copy" style="margin:2px 7px 0px 0px"/> Copy &#8680;</div>
                  <div v-else-if="status === 'creating'" class="spinner-border text-primary">
                      <span class="sr-only">Creating...</span>
                  </div>
                  <div v-else-if="status === 'created'">
                    <span class="status-success status-icon">&check;</span>
                  </div>
                </div>
                <div class="col col-12 col-md-5">
                  <div v-if="item">
                    {{ item.payee_name }} {{ _toLocalTime(item.issue_date, defaultDateFormat) }}
                    <PayoutsLinks
                      v-if="item"
                      :item="item"
                      @updatePayouts="updatePayouts"
                      @showEditPayoutsModal="showPayoutModalManual(copyExtraPaymentedPayouts[index])"
                      @markAsPaid="markPaid($event, item)"
                    />
                  </div>
                  <div v-else>
                    No payout for {{ original.payee_name }} at {{ Moment().format('MMM YYYY') }}
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              No payouts
            </template>
          </b-card>
        </b-collapse>
      </template>
      <template v-else>
        <div class="loading-message">
          <h5>Loading, please wait</h5>
        </div>
        <Preloader />
      </template>
    </b-modal>

    <FinancePayoutsModal
      ref="payoutModalManual"
      v-model="payoutModalManual.show"
      :mode="payoutModalManual.data.id ? 'edit' : 'create'"
      :data="payoutModalManual.data"
      :title="payoutModalManual.data.id ? 'Save Payout' : 'Create Payout'"
      :submitButtonText="payoutModalManual.data.id ? 'Save' : 'Create'"
      @submit="savePayoutManually"
    />

    <FinancePayoutsModal
      ref="payoutModalAuto"
      :value="false"
      mode="create"
    />
    <FinancePayoutsModal
      ref="payoutModalCopy"
      :value="false"
      mode="create"
    />
  </div>
</template>

<script>
import { cloneDeep, debounce } from 'lodash'
import Moment from 'moment'
import axios from 'axios'
import Datepicker from 'vuejs-datepicker'
import { mapActions } from 'vuex'
import { permissions } from '@mocks/permissions'
import Preloader from '@components/Preloader'
import FinancePayoutsModal from '@components/modals/FinancePayoutsModal/FinancePayoutsModal'
import PayoutsLinks from '../components/PayoutsLinks'

export default {
  name: 'FinancePayoutsBulkCreation',
  components: {
    Datepicker,
    Preloader,
    FinancePayoutsModal,
    PayoutsLinks
  },
  props: {
    templates: {
      type: Array
    }
  },
  data () {
    const defaultDateFormat = 'YYYY-MM-DD'
    return {
      permissions,
      defaultDateFormat,
      isDataPageLoading: false,
      isShowCreationModal: false,
      isProcessedCreation: false,
      openedPayoutMessage: null,
      payoutModalManual: {
        show: false,
        data: {}
      },
      isRatesLoading: false,
      isDataLoading: false,
      dateFrom: this._toLocalTime(Moment().subtract(1, 'months').startOf('month')),
      dateTo: this._toLocalTime(Moment().subtract(1, 'months').endOf('month')),
      currencyRates: {
        UAH: null, EUR: null, GBP: null
      },
      dataForCreation: [],
      copyExtraPaymentedPayouts: [],
      messages: []
    }
  },
  computed: {
    payeeDataWithoutCompensation () {
      return this.dataForCreation.filter(item => item.no_compensation)
    },
    payoutsForCreationCount () {
      return this.dataForCreation.filter(item => !item.no_compensation && !item.processed).length
    },
    currentProcessedPayoutNumber () {
      return this.dataForCreation.filter(item => !item.no_compensation && item.processed).length + 1
    },
    isSomePayoutCreating () {
      return this.messages.some(item => item.loading)
    }
  },
  created () {
    this.fetchActualRate()
  },
  methods: {
    Moment: function (date) {
      return Moment(date)
    },
    ...mapActions({
      setIsModalLoading: 'finances/setIsModalLoading'
    }),
    async generatePayouts () {
      this.isShowCreationModal = true

      this.dataForCreation = []
      this.copyExtraPaymentedPayouts = []
      this.messages = []

      this.isDataLoading = true
      const dataForCreationResponse = await this.fetchDataForCreation()
      this.isDataLoading = false

      this.dataForCreation = dataForCreationResponse.data.genByWorklogs.map(item => {
        item.processed = false
        return item
      })
      this.copyExtraPaymentedPayouts = dataForCreationResponse.data.copyExtraPaymented.map(item => {
        return {
          original: item,
          item: null,
          status: 'new'
        }
      })

      this.messages.push({
        type: 'info',
        text: 'Fetched <b>' + this.dataForCreation.length + ' payees</b> (contractors and partners) for creation at period: ' + this._toLocalTime(this.dateFrom, this.defaultDateFormat) + ' - ' + this._toLocalTime(this.dateTo, this.defaultDateFormat),
        status: 'success'
      })

      if (this.payeeDataWithoutCompensation.length > 0) {
        const payeeNamesWithoutCompensations = this.payeeDataWithoutCompensation.map(item => {
          if (item.contractor) {
            return item.contractor.name
          }
          if (item.partner) {
            return '[Partner] ' + item.partner.company_name
          }
        })

        this.messages.push({
          type: 'info',
          status: 'warning',
          text: 'Attention! Payees: <b>' + payeeNamesWithoutCompensations.join(', ') + '</b> has no active compensation at period: ' +
            this._toLocalTime(this.dateFrom, this.defaultDateFormat) + ' - ' + this._toLocalTime(this.dateTo, this.defaultDateFormat) + '. Please create compensations and "Generate Payouts" again.'
        })
      }
    },
    async hideCreationModal (event) {
      if (event.trigger !== 'confirmed') {
        event.preventDefault()
      }
      const confirm = await this.$root.$confirm.open(
        'Close Bulk Creation',
        'Are you sure you want to stop bulk creation process and close this window?'
      )
      if (confirm) {
        event.vueTarget.hide('confirmed')
      }

      this.isProcessedCreation = false
      if (this.messages.filter(item => item.type === 'creation' && item.status === 'success').length > 0) {
        this.updatePayouts()
      }
    },
    updatePayouts () {
      this.$emit('updatePayouts')
    },
    saveMessageSuccess (message, item) {
      message.status = 'success'
      message.item = item ? Object.assign(message.item, item) : message.item
      message.errors = []
      message.loading = false
    },
    saveMessageError (message, errors, item) {
      message.status = 'error'
      message.errors = message.errors ? message.errors.push(errors) : errors
      message.loading = false
      message.item = item ? Object.assign(message.item, item) : message.item
    },
    async processCreation () {
      this.isProcessedCreation = true

      const dateFrom = this.dateFrom
      const dateTo = this.dateTo

      for (const index in this.dataForCreation) {
        const item = this.dataForCreation[index]
        if (!this.isProcessedCreation) {
          break
        }
        if (item.processed || item.no_compensation) {
          continue
        }

        const payeeName = item.partner ? '[Partner] ' + item.partner.company_name : item.contractor.name
        const contractorId = item.contractor ? item.contractor.id : null
        const partnerId = item.partner ? item.partner.id : null

        this.messages.push({
          type: 'creation',
          status: 'in-progress',
          text: this.currentProcessedPayoutNumber + '. Creating  <b>' + payeeName + '</b> payout for actual period: ' + this._toLocalTime(dateFrom, this.defaultDateFormat) + ' - ' + this._toLocalTime(dateTo, this.defaultDateFormat),
          loading: true
        })

        item.processed = true
        this.dataForCreation[index] = item

        let processedPayoutMessage = this.messages.at(-1)

        const preFillData = await this.$refs['payoutModalAuto'].fetchPreFillPayoutData(contractorId, partnerId)

        let payoutData = preFillData.data
        payoutData.items = []
        payoutData.auto_fill = { date_from: dateFrom, date_to: dateTo }

        payoutData.comment = this.$refs['payoutModalAuto'].generateCommentText(payoutData.auto_fill, 'comment')
        payoutData.comment_national = this.$refs['payoutModalAuto'].generateCommentText(payoutData.auto_fill, 'comment_national')

        payoutData.payout_status = this.getPayoutStatus('new')
        processedPayoutMessage.item = payoutData

        let errors = []
        if (!payoutData.payee_bank_details) {
          errors.push('Payee bank details is required')
        }

        if (!payoutData.own_bank_details) {
          errors.push('Own bank details is required')
        }

        if (errors.length > 0) {
          this.saveMessageError(processedPayoutMessage, errors, payoutData)
          continue
        }

        const currencyCode = payoutData.payee_bank_details.currency.code
        const currencyRate = this.currencyRates[currencyCode]
        payoutData.currency_rate = currencyRate.toString()

        this.$refs['payoutModalAuto'].setParamsForEdit(payoutData)

        if (payoutData.payout_type.id === 1) {
          const autoFillData = await this.axios.post(
            `/api/finances/payouts/auto-fill-request`,
            {
              payout_id: null, contractor_id: contractorId, partner_id: partnerId, date_from: dateFrom, date_to: dateTo, currency_code: currencyCode, currency_rate: currencyRate
            },
            { headers: { Authorization: 'Bearer' + localStorage.getItem('access_token') } }
          )

          if (autoFillData.data.length) {
            payoutData.items = payoutData.contractors = autoFillData.data
            await this.$refs['payoutModalAuto'].autoFillItems(autoFillData.data)
          } else {
            this.saveMessageError(processedPayoutMessage, ['No contractor payout items at period'], payoutData)
            continue
          }
        }
        const payoutDataForRequest = this.$refs['payoutModalAuto'].getPreparedDataForRequest()

        payoutData.indicators = cloneDeep(this.$refs['payoutModalAuto'].form.fields.indicators.value)
        processedPayoutMessage.item = payoutData
        await this.savePayoutBulk(payoutDataForRequest, processedPayoutMessage)
      }
    },
    async savePayoutBulk (payoutDataForRequest, payoutMessage) {
      return this.savePayout(payoutDataForRequest, payoutMessage, async function () {
        if (payoutDataForRequest.id) {
          return axios.put(`/api/finances/payouts/${payoutDataForRequest.id}`, payoutDataForRequest, {
            headers: { Authorization: 'Bearer' + localStorage.getItem('access_token') }
          })
        } else {
          return axios.post(`/api/finances/payouts`, payoutDataForRequest, {
            headers: { Authorization: 'Bearer' + localStorage.getItem('access_token') }
          })
        }
      })
    },
    async savePayoutManually (payoutDataForRequest) {
      const that = this
      await this.savePayout(payoutDataForRequest, this.openedPayoutMessage, async function () {
        if (payoutDataForRequest.id) {
          return that._toggleDataPageLoading(async () => {
            return that.$api.finances.updatePayouts(payoutDataForRequest)
          })
        } else {
          return that._toggleDataPageLoading(async () => {
            return that.$api.finances.createPayouts(payoutDataForRequest)
          })
        }
      })
      this.payoutModalManual.show = false
    },
    async savePayout (payoutDataForRequest, payoutMessage, savePayoutFunc) {
      try {
        let payoutResponse
        payoutResponse = await savePayoutFunc()
        const payoutData = payoutResponse.data
        payoutData.auto_fill = payoutMessage.item.auto_fill

        if (payoutResponse.status === 200) {
          this.saveMessageSuccess(payoutMessage, payoutData)
        }
      } catch (e) {
        const errors = []
        if (e.response.data.errors) {
          errors.push(Object.values(e.response.data.errors).map(itemErrors => itemErrors[0]))
        } else {
          errors.push(e.message)
        }

        this.saveMessageError(payoutMessage, errors)
      }
    },
    async markPaid (payout, item) {
      if (!payout.id) return
      this._toggleDataPageLoading(async () => {
        const marked = await this.$api.finances.markPayoutPaid(payout.id)
        if (marked.data) {
          this.$snotify.success(
            'Payout successfully marked as paid',
            'Success',
            this.$store.state.snotify
          )
          item.payout_status = this.getPayoutStatus('paid')
          item.is_paid = true
        }
      })
    },
    getPayoutStatus (name) {
      return this.$refs['payoutModalAuto'].getPayoutsStatuses.find(el => el.name === name)
    },
    fetchActualRate: debounce(function () {
      this.isRatesLoading = true
      this.axios
        .get('/api/finances/currencies/actual-rates-for-date', {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then((response) => {
          this.currencyRates = response.data
        })
        .catch((error) => {
          this.errorNotifications(error)
        })
        .finally(() => {
          this.isRatesLoading = false
        })
    }, 1500),
    fetchDataForCreation () {
      return this.axios
        .post('/api/finances/payouts/data-for-bulk-creation',
          {
            date_from: this._toLocalTime(this.dateFrom),
            date_to: this._toLocalTime(this.dateTo)
          },
          { headers: { Authorization: 'Bearer' + localStorage.getItem('access_token') } })
        .catch((error) => {
          this.errorNotifications(error)
        })
    },
    async showPayoutModalManual (message) {
      if (this.$can(this.permissions.UpdateAllFinancesPayouts)) {
        this.openedPayoutMessage = message
        this.payoutModalManual.show = true
        this.payoutModalManual.data = await cloneDeep(message.item)

        this.$refs['payoutModalManual'].validateForm()
      }
    },
    async copyPayout (index) {
      const original = this.copyExtraPaymentedPayouts[index].original
      this.copyExtraPaymentedPayouts[index].status = 'creating'
      const item = cloneDeep(original)
      item.payout_status = this.getPayoutStatus('new')
      this.$refs['payoutModalCopy'].setParamsForEdit(item)

      const payoutDataForRequest = this.$refs['payoutModalCopy'].getPreparedDataForRequest()
      const payoutIntervalDates = {
        date_from: this.dateFrom,
        date_to: this.dateTo
      }
      payoutDataForRequest.comment = this.$refs['payoutModalCopy'].generateCommentText(payoutIntervalDates, 'comment')
      payoutDataForRequest.comment_national = this.$refs['payoutModalCopy'].generateCommentText(payoutIntervalDates, 'comment_national')
      // payoutDataForRequest.items = []
      // payoutDataForRequest.payout_type_id = 2
      payoutDataForRequest.issue_date = this.Moment().format(this.defaultDateFormat)
      payoutDataForRequest.due_date = this.Moment().add(3, 'days').format(this.defaultDateFormat)
      payoutDataForRequest.paid_date = ''

      const payoutResponse = await this._toggleDataPageLoading(async () => {
        return this.$api.finances.createPayouts(payoutDataForRequest)
      })
      this.copyExtraPaymentedPayouts[index].item = payoutResponse.data
      this.copyExtraPaymentedPayouts[index].status = 'created'
    },
    indicatorTooltip (items) {
      const itemsAsString = Object.entries(items).map(
        ([name, hours]) => {
          return name + ' (working: ' + hours.working_hours + '; logged: ' + hours.logged_hours + ')'
        }
      )
      return itemsAsString.join(', ')
    }
  }
}
</script>

<style lang="scss">
  .payout-bulk-generation {
    padding: 20px;
    background: #E4E7EE;
    border-radius: 10px;
    margin-bottom: 30px;
  }
  .status-icon {
    font-size: x-large;
  }
  .status-error {
    color: red;
  }
  .status-success {
    color: #3fa52f;
  }
  .status-warning {
    color: red;
  }
  .small-spinner.spinner-border {
    width: 1rem;
    height: 1rem;
  }
  .message-item {
    display: flex;
  }
  .message-item-right-box {
    margin-left: auto;
  }
  .loading-message {
    text-align:center;
  }
  .ep-message {
    margin-left: 20px;
  }
</style>
